import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateHazardControl } from '../Hazard.hooks';
import {
  BaseHazardControlsCardProps,
  HazardControl
} from '../Hazard.interface';

const HazardControlInfoCard: React.FC<BaseHazardControlsCardProps> = (
  props: BaseHazardControlsCardProps
) => {
  const {
    title = 'Details',
    hazardId,
    hazardControl,
    hazardControlVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = hazardControlVersion || hazardControl;
  const initialValues: Partial<HazardControl> = {
    title: initialData.title || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/hazards/${hazardId}/causes/${hazardControl.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedHazardControl,
    isUpdatingHazardControl: isUpdating,
    updateHazardControlError: updateError,
    updateHazardControl
  } = useUpdateHazardControl(hazardId, hazardControl.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(hazardControl.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(hazardControl.task);

  // notify parent when a user need info is updated
  useEffect(() => {
    if (updatedHazardControl) {
      onChange?.(updatedHazardControl);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedHazardControl]);

  useEffect(() => {
    if (updatedTask) {
      hazardControl.task = Object.assign(hazardControl.task || {}, updatedTask);
      onChange?.(hazardControl);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <InlineFormik
      initialValues={initialValues}
      isSubmitting={isUpdating}
      error={updateError}
      onSubmit={(values: Partial<HazardControl>) => {
        updateHazardControl(values);
        onChange?.(values);
      }}
    >
      <Card style={hidden ? { display: 'none' } : undefined}>
        <CardTitle>
          <SubjectIcon />
          {enableTitleRedirect ? (
            <Link className='link' to={getPath()}>
              {title}
            </Link>
          ) : (
            title
          )}
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineTextField
              name='title'
              label='Title'
              placeholder='Enter title for Hazard Control'
              disabled={
                Boolean(hazardControlVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText='Provide an overview of the control.'
              disabled={
                Boolean(hazardControlVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={
                hazardControlVersion?.attachments ||
                hazardControl.task.attachments ||
                []
              }
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (hazardControl.task) {
                  hazardControl.task.attachments = attachments;
                }
                onChange?.(hazardControl);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};

export default HazardControlInfoCard;
